import React, { useEffect, useState } from "react";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import { GraduationCap, Search, Filter, MapPin, Calendar, Edit, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function ScholarshipList() {
  const [scholarshipData, setScholarshipData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    id: "",
    name: "",
    countryName: "",
    startDate: "",
    endDate: "",
    scholarshipType: "",
    description: ""
  });
  const navigate = useNavigate();


  const fetchScholarshipsByAcademicYear = async (academicYearId) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL +
          `hec/abroad/find-scholarships?academicYear=${academicYearId}`
      );
      setScholarshipData(response.data || []);
    } catch (error) {
      //setError("Failed to fetch scholarships");
      setScholarshipData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScholarshipsByAcademicYear(new Date().getFullYear()-1);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

 const filteredScholarships = scholarshipData
    .filter(
        (scholarship) =>
            scholarship.scholarshipType.toLowerCase().includes(searchTerm?.toLowerCase())
    )
    .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));

    const handleScholarshipTypeChange = (type) => {
    if (type === "") {
      setSearchTerm("");
      return;
    }
    setSearchTerm(type);
  };

  const handleAddPrograms = (scholarshipId) => {
    navigate(`/add_education_program/${scholarshipId}`);
  };

  const handleUpdateClick = (scholarship) => {
    setSelectedScholarship(scholarship);
    setUpdateFormData({
      id: scholarship.id || "",
      name: scholarship.name || "",
      countryName: scholarship.countryName || "",
      startDate: scholarship.startDate ? scholarship.startDate.split('T')[0] : "",
      endDate: scholarship.endDate ? scholarship.endDate.split('T')[0] : "",
      scholarshipType: scholarship.scholarshipType || "",
      description: scholarship.description || ""
    });
    setShowUpdateModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);


    
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/abroad/update`,
        updateFormData,
        { headers: getAuthHeaders() }
      );
      
      // Update the local state with the updated scholarship
      setScholarshipData(prev => 
        prev.map(scholarship => 
          scholarship.id === selectedScholarship.id 
            ? { ...scholarship, ...updateFormData }
            : scholarship
        )
      );
      
      setShowUpdateModal(false);
      setSelectedScholarship(null);
      
      // Show success toast notification
      toast.success("Scholarship updated successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      console.error("Error updating scholarship:", error);
      
      // Show error toast notification
      const errorMessage = error.response?.data?.message || "Failed to update scholarship. Please try again.";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowUpdateModal(false);
    setSelectedScholarship(null);
    setUpdateFormData({
      id: "",
      name: "",
      countryName: "",
      startDate: "",
      endDate: "",
      scholarshipType: "",
      description: ""
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="bg-gray-50">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="p-4 sm:ml-64">
        <div className="p-4 border-2 border-white border-b rounded-lg dark:border-gray-700">
          <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                    <GraduationCap className="w-8 h-8 text-cyan-600" />
                  </div>
                  <p className="text-cyan-600">Loading scholarships...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                  <p className="font-medium">We can not find the Scholarships in that academic year {error}</p>
                  <p>Please try again later.</p>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h1
                      className="text-3xl font-bold mb-2"
                      style={{ color: "#016b85" }}
                    >
                      Abroad Scholarships
                    </h1>
                    <p style={{ color: "#016b85" }}>
                      Available scholarships for students planning to
                      study abroad. Use the search bar to find scholarships by
                      name or country.
                    </p>
                  </div>

                  {/* Search Bar */}
                  
                    <div
                        className="bg-white rounded-lg shadow-sm border p-6 mb-6"
                        style={{ borderColor: "#b2dbe3" }}
                    >
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label htmlFor="scholarshipType" className="block text-sm font-medium mb-2 text-cyan-800">
                                                Scholarship Type
                                </label>
                                <select
                                    id="scholarshipType"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    style={{ borderColor: "#b2dbe3" }}
                                    onChange={(e) => {
                                                    // Add your handler function here
                                            handleScholarshipTypeChange(e.target.value);
                                                }}>
                                                <option value="">Select Scholarship Type</option>
                                                <option value="GOVERNMENT">GOVERNMENT OF RWANDA</option>
                                                <option value="COOPERATION">COOPERATION</option>
                                            </select>
                            </div>
                        </div>
                    </div>
                  {/* Results Counter */}
                  <div className="mb-4">
                    <p className="text-sm" style={{ color: "#016b85" }}>
                      Displaying {filteredScholarships.length} of{" "}
                      {scholarshipData.length} available scholarships
                    </p>
                  </div>

                  {/* Table */}
                  <div
                    className="bg-white rounded-lg shadow-sm border overflow-hidden"
                    style={{ borderColor: "#b2dbe3" }}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead style={{ background: "#155e75" }}>
                          <tr>
                            <th
                              className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider"
                              style={{ color: "#ffffff" }}
                            >
                              Scholarship Name
                            </th>
                            <th
                              className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider"
                              style={{ color: "#ffffff" }}
                            >
                              Country
                            </th>
                            <th
                              className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider"
                              style={{ color: "#ffffff" }}
                            >
                              Start Date
                            </th>
                            <th
                              className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider"
                              style={{ color: "#ffffff" }}
                            >
                              End Date
                            </th>
                            {/* <th
                              className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider"
                              style={{ color: "#ffffff" }}
                            >
                              Requirements
                            </th> */}
                            <th
                              className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider"
                              style={{ color: "#ffffff" }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody
                          className="bg-white divide-y"
                          style={{ borderColor: "#e6f3f6" }}
                        >
                          {filteredScholarships?.map((scholarship) => (
                            <tr
                              key={scholarship.id}
                              className="hover:bg-[#e6f3f6] transition-colors duration-150"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    <div
                                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                                      style={{ background: "#e6f3f6" }}
                                    >
                                      <GraduationCap
                                        className="w-5 h-5"
                                        style={{ color: "#155e75" }}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <div
                                      className="text-sm font-medium leading-tight pt-3"
                                      style={{ color: "#155e75" }}
                                    >
                                      {scholarship.name}
                                    </div>
                                    {/* <div
                                      className="text-xs mt-1"
                                      style={{ color: "#7bb8c6" }}
                                    >
                                      ID: {scholarship.id.substring(0, 8)}...
                                    </div> */}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <MapPin
                                    className="w-4 h-4"
                                    style={{ color: "#7bb8c6" }}
                                  />
                                  <div
                                    className="text-sm"
                                    style={{ color: "#155e75" }}
                                  >
                                    {scholarship.countryName}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <Calendar
                                    className="w-4 h-4"
                                    style={{ color: "#7bb8c6" }}
                                  />
                                  <div
                                    className="text-sm"
                                    style={{ color: "#155e75" }}
                                  >
                                    {formatDate(scholarship.startDate)}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <Calendar
                                    className="w-4 h-4"
                                    style={{ color: "#7bb8c6" }}
                                  />
                                  <div
                                    className="text-sm"
                                    style={{ color: "#155e75" }}
                                  >
                                    {formatDate(scholarship.endDate)}
                                  </div>
                                </div>
                              </td>
                              {/* <td className="px-6 py-4">
                                <div className="text-sm">
                                  <ul className="space-y-1">
                                    {scholarship.requirements.map((req) => (
                                      <li
                                        key={req.id}
                                        className="flex items-center space-x-2"
                                      >
                                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span>
                                        <span style={{ color: "#155e75" }}>
                                          {req.name}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </td> */}
                              <td className="px-6 py-4">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      handleAddPrograms(scholarship.id)
                                    }
                                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-800 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-colors"
                                  >
                                    <GraduationCap className="w-4 h-4 mr-2" />
                                    Add Programs
                                  </button>
                                  <button
                                    onClick={() => handleUpdateClick(scholarship)}
                                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Update
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {filteredScholarships.length === 0 && (
                      <div className="text-center py-12">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                          style={{ background: "#e6f3f6" }}
                        >
                          <Search
                            className="w-8 h-8"
                            style={{ color: "#7bb8c6" }}
                          />
                        </div>
                        <h3
                          className="text-lg font-medium mb-2"
                          style={{ color: "#016b85" }}
                        >
                          No scholarships found
                        </h3>
                        <p style={{ color: "#7bb8c6" }}>
                          Please refine your search criteria or clear the search
                          to view all available scholarships.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Update Modal */}
        {showUpdateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium" style={{ color: "#155e75" }}>
                    Update Scholarship
                  </h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleUpdateSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#155e75" }}>
                      Scholarship Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={updateFormData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#155e75" }}>
                      Country Name
                    </label>
                    <input
                      type="text"
                      name="countryName"
                      value={updateFormData.countryName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#155e75" }}>
                      Scholarship Type
                    </label>
                    <select
                      name="scholarshipType"
                      value={updateFormData.scholarshipType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      required
                    >
                      <option value="">Select Scholarship Type</option>
                      <option value="GOVERNMENT">GOVERNMENT OF RWANDA</option>
                      <option value="COOPERATION">COOPERATION</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#155e75" }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={updateFormData.startDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#155e75" }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={updateFormData.endDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#155e75" }}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={updateFormData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-vertical"
                      placeholder="Enter scholarship description..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateLoading}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-800 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
                    >
                      {updateLoading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        <div>
          <FooterComponents />
        </div>
      </div>
    </div>
  );
}

export default ScholarshipList;
